// /frontend/src/pages/ClientProfile.jsx
import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams, Link } from "react-router-dom";
import MessageThread from "../components/MessageThread";
import EmailComposer from "../components/EmailComposer";
import ClientScheduleModal from "../components/ClientScheduleModal";
import ClientDetailCard from "../components/ClientDetailCard";
import CommunicationsHub from "../components/communications/CommunicationsHub";
import ClientEditModal from "../components/ClientEditModal";
import RiskScoreCard from "../components/RiskScoreCard";
import ConfidencePulseCard from "../components/ConfidencePulseCard";
import CallOutcomeModal from "../components/CallOutcomeModal";
import HealthDataSection from "../components/HealthDataSection";
import ClientSnapshot from "../components/ClientSnapshot";
import OtherPoliciesCard from "../components/OtherPoliciesCard";
import PlanHistoryCard from "../components/policies/PlanHistoryCard";
import ServiceTab from "../components/ServiceTab";
import ClientCommissionSection from "../components/ClientCommissionSection";
import { getClientById, addComm, updateClient, getBlueButtonStatus, disconnectBlueButton, syncBlueButtonData, sendBlueButtonLinkEmail, sendBlueButtonLinkSMS, uploadClientPhoto, matchPatientAccessCarrier, sendCarrierLinkEmail, sendCarrierLinkSMS } from "../api";
import { Phone, MessageCircle, Mail, CalendarClock, CheckSquare, Heart, RefreshCw, Unlink, UserMinus, X, Users, ChevronRight, CheckCircle, Camera, Headphones } from "lucide-react";
import CallTypeModal from "../components/CallTypeModal";

const ClientProfile = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const [client, setClient] = useState(null);
  const [showSms, setShowSms] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showComms, setShowComms] = useState(false);
  const [showTask, setShowTask] = useState(false);
  const [showLostModal, setShowLostModal] = useState(false);
  const [lostReason, setLostReason] = useState("");
  const [taskData, setTaskData] = useState({ title: "", notes: "", priority: "normal", dueDate: "" });
  const [clientTasks, setClientTasks] = useState([]);
  const [savingClient, setSavingClient] = useState(false);
  const [showCallOutcome, setShowCallOutcome] = useState(false);
  const [showCallTypeModal, setShowCallTypeModal] = useState(false);

  // Health Data state
  const [bbStatus, setBbStatus] = useState(null);
  const [bbLoading, setBbLoading] = useState(true);
  const [bbSyncing, setBbSyncing] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [connectModalStep, setConnectModalStep] = useState('choose'); // 'choose' | 'method'
  const [selectedSource, setSelectedSource] = useState(null); // 'bluebutton' | carrier object
  const [matchedCarrier, setMatchedCarrier] = useState(null); // carrier that matches client's carrier
  const [sendingLink, setSendingLink] = useState(null);
  const [linkSent, setLinkSent] = useState(null);
  const [connectError, setConnectError] = useState(null);

  // Photo upload
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef(null);

  // Check for Health Data callback status
  useEffect(() => {
    const bbResult = searchParams.get('bluebutton');
    if (bbResult === 'connected') {
      alert(t('healthConnectedSuccess'));
      getBlueButtonStatus(id).then(status => setBbStatus(status)).catch(() => {});
    } else if (bbResult === 'error') {
      const message = searchParams.get('message') || 'Unknown error';
      alert(`${t('healthConnectionFailed')}: ${message}`);
    }
  }, [searchParams, id, t]);

  // Load Health Data status
  useEffect(() => {
    let live = true;
    async function loadBbStatus() {
      if (!id) return;
      try {
        setBbLoading(true);
        const status = await getBlueButtonStatus(id);
        if (live) setBbStatus(status);
      } catch (err) {
        console.error('Health data status error:', err);
      } finally {
        if (live) setBbLoading(false);
      }
    }
    loadBbStatus();
    return () => { live = false; };
  }, [id]);

  // Fetch tasks for this client
  useEffect(() => {
    if (!id) return;
    const BASE = process.env.REACT_APP_API_URL || "";
    function fetchTasks() {
      fetch(`${BASE}/tasks/clients/${id}`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : Promise.reject())
        .then(json => { if (json.ok) setClientTasks(json.data || []); })
        .catch(() => setClientTasks([]));
    }
    fetchTasks();
    window.addEventListener('tasks:update', fetchTasks);
    return () => window.removeEventListener('tasks:update', fetchTasks);
  }, [id]);

  // Health Data handlers
  function handleConnectHealth() {
    setShowConnectModal(true);
    setConnectModalStep('choose');
    setSelectedSource(null);
    setLinkSent(null);
    setConnectError(null);
  }

  function handleSelectSource(source) {
    setSelectedSource(source);
    setConnectModalStep('method');
  }

  function handleBackToSourceChoice() {
    setConnectModalStep('choose');
    setSelectedSource(null);
    setConnectError(null);
  }

  // Send link via email
  async function handleSendEmail() {
    try {
      setSendingLink('email');
      setConnectError(null);

      let result;
      if (selectedSource === 'bluebutton') {
        result = await sendBlueButtonLinkEmail(id);
      } else if (selectedSource?.id) {
        // Carrier object
        result = await sendCarrierLinkEmail(selectedSource.id, id);
      } else {
        throw new Error('No source selected');
      }

      if (result.ok) {
        setLinkSent('email');
      } else {
        setConnectError(result.error || 'Failed to send email');
      }
    } catch (err) {
      setConnectError(err.message);
    } finally {
      setSendingLink(null);
    }
  }

  // Send link via SMS
  async function handleSendSMS() {
    try {
      setSendingLink('sms');
      setConnectError(null);

      let result;
      if (selectedSource === 'bluebutton') {
        result = await sendBlueButtonLinkSMS(id);
      } else if (selectedSource?.id) {
        // Carrier object
        result = await sendCarrierLinkSMS(selectedSource.id, id);
      } else {
        throw new Error('No source selected');
      }

      if (result.ok) {
        setLinkSent('sms');
      } else {
        setConnectError(result.error || 'Failed to send SMS');
      }
    } catch (err) {
      setConnectError(err.message);
    } finally {
      setSendingLink(null);
    }
  }

  // Photo upload handler
  async function handlePhotoUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be less than 5MB');
      return;
    }

    try {
      setUploadingPhoto(true);
      const result = await uploadClientPhoto(id, file);
      if (result.ok && result.photoUrl) {
        // Update client state with new photo
        setClient(prev => ({ ...prev, photoUrl: result.photoUrl }));
      }
    } catch (err) {
      alert('Failed to upload photo: ' + err.message);
    } finally {
      setUploadingPhoto(false);
      // Reset file input
      if (photoInputRef.current) {
        photoInputRef.current.value = '';
      }
    }
  }

  async function handleSyncHealth() {
    try {
      setBbSyncing(true);
      const result = await syncBlueButtonData(id);
      const status = await getBlueButtonStatus(id);
      setBbStatus(status);
      const data = await getClientById(id);
      setClient(data);
      alert(`Sync complete! ${result.claimsFetched} claims fetched.`);
    } catch (err) {
      alert('Sync failed: ' + err.message);
    } finally {
      setBbSyncing(false);
    }
  }

  async function handleDisconnectHealth() {
    try {
      await disconnectBlueButton(id);
      setBbStatus({ connected: false });
      setShowDisconnectConfirm(false);
    } catch (err) {
      alert('Disconnect failed: ' + err.message);
    }
  }

  useEffect(() => {
    let live = true;
    async function load() {
      try {
        const data = await getClientById(id);
        if (live) setClient(data);
      } catch {}
    }
    if (id) load();
    return () => { live = false; };
  }, [id]);

  // Load matched carrier for Patient Access
  useEffect(() => {
    if (!client?.carrier) {
      setMatchedCarrier(null);
      return;
    }
    matchPatientAccessCarrier(client.carrier)
      .then(result => {
        if (result?.carrier) {
          setMatchedCarrier(result.carrier);
        }
      })
      .catch(() => setMatchedCarrier(null));
  }, [client?.carrier]);

  if (!client) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-10 font-[Inter]">
        <div className="text-slate-500">{t('loading')}</div>
      </div>
    );
  }

  const clientStatus = client.status || (client.effectiveDate ? 'active' : 'prospect');

  const customerSince = (() => {
    if (!client.effectiveDate) return null;
    const start = new Date(client.effectiveDate);
    if (isNaN(start.getTime())) return null;
    const now = new Date();
    const diffTime = now - start;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return t('startsSoon');
    if (diffDays === 0) return t('today');
    if (diffDays < 30) return `${diffDays} ${diffDays !== 1 ? t('days') : t('day')}`;
    const months = Math.floor(diffDays / 30);
    if (months < 12) return `${months} ${months !== 1 ? t('months') : t('month')}`;
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    if (remainingMonths === 0) return `${years} ${years !== 1 ? t('years') : t('year')}`;
    return `${years} ${years !== 1 ? t('years') : t('year')}, ${remainingMonths} ${remainingMonths !== 1 ? t('months') : t('month')}`;
  })();

  function onSendTextDemo() { setShowSms(true); }
  function onSendEmailDemo() { setShowEmail(true); }
  function onScheduleDemo() {
    setShowSchedule(true);
  }
  function onCallDemo() {
    if (!client.phone) {
      alert(t('noPhoneOnFile'));
      return;
    }
    setShowCallTypeModal(true);
  }

  async function handleInitiateCall({ record = false }) {
    // This initiates the Twilio click-to-call feature
    // If Twilio isn't configured, user can still call manually and log outcome
    try {
      const BASE = process.env.REACT_APP_API_URL || "";
      const res = await fetch(`${BASE}/calls/initiate`, {
        method: "POST",
        credentials: 'include',
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clientId: client.id, clientPhone: client.phone, record })
      });
      const data = await res.json();
      if (data.ok) {
        console.log('Twilio call initiated:', data.message);
      } else {
        console.log('Twilio call not initiated:', data.error);
        // Don't show alert - user can still call manually and log outcome
      }
    } catch (error) {
      console.log("Click-to-call not available:", error.message);
      // Don't show alert - user can still call manually and log outcome
    }
  }

  async function handleSaveClient(updatedData) {
    setSavingClient(true);
    try {
      const updated = await updateClient(client.id, updatedData);
      setClient(updated);
      setShowEdit(false);
      alert(t('clientUpdated'));
    } catch (error) {
      console.error("Update client error:", error);
      alert(`${t('failedToUpdate')}: ${error.message}`);
    } finally {
      setSavingClient(false);
    }
  }

  async function handleMarkAsLost() {
    if (!lostReason.trim()) {
      alert(t('provideReasonLost'));
      return;
    }
    try {
      const updated = await updateClient(client.id, {
        status: 'lost',
        notes: client.notes
          ? `${client.notes}\n\n${t('lostCustomerPrefix')} (${new Date().toLocaleDateString()}) ---\n${lostReason.trim()}`
          : `${t('lostCustomerPrefix')} (${new Date().toLocaleDateString()}) ---\n${lostReason.trim()}`
      });
      setClient(updated);
      setShowLostModal(false);
      setLostReason("");
      alert(t('clientMarkedLost'));
    } catch (error) {
      console.error("Mark as lost error:", error);
      alert(`${t('failedToUpdate')}: ${error.message}`);
    }
  }

  async function handleReactivateClient() {
    if (!window.confirm(t('reactivateConfirm'))) return;
    try {
      const updated = await updateClient(client.id, { status: 'active' });
      setClient(updated);
      alert(t('clientReactivated'));
    } catch (error) {
      console.error("Reactivate error:", error);
      alert(`${t('failedToReactivate')}: ${error.message}`);
    }
  }

  async function saveTask() {
    if (!taskData.title.trim()) return;
    try {
      const BASE = process.env.REACT_APP_API_URL || "";
      const res = await fetch(`${BASE}/tasks/clients/${client.id}`, {
        method: "POST",
        credentials: 'include',
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: taskData.title.trim(),
          notes: taskData.notes.trim(),
          priority: taskData.priority,
          dueDate: taskData.dueDate ? new Date(taskData.dueDate).toISOString() : null
        })
      });
      if (!res.ok) throw new Error("Failed to create task");
      setTaskData({ title: "", notes: "", priority: "normal", dueDate: "" });
      setShowTask(false);
      window.dispatchEvent(new CustomEvent("tasks:update"));
      alert(t('taskCreated'));
    } catch (error) {
      console.error("Create task error:", error);
      alert(t('failedToCreateTask'));
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="max-w-7xl mx-auto px-4 py-8 font-[Inter]">
        {/* Header with gradient background */}
        <div className="bg-gradient-to-r from-[#172A3A] to-slate-800 rounded-2xl shadow-xl p-8 mb-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                {/* Avatar with photo upload */}
                <div className="relative group">
                  {client.photoUrl ? (
                    <img
                      src={`${process.env.REACT_APP_API_URL || ''}${client.photoUrl}`}
                      alt={`${client.firstName} ${client.lastName}`}
                      className="w-16 h-16 rounded-full object-cover shadow-lg"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#FFB800] to-amber-500 flex items-center justify-center text-2xl font-bold text-white shadow-lg">
                      {(client.firstName?.[0] || "") + (client.lastName?.[0] || "")}
                    </div>
                  )}
                  {/* Upload overlay */}
                  <button
                    onClick={() => photoInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                  >
                    {uploadingPhoto ? (
                      <RefreshCw className="w-6 h-6 text-white animate-spin" />
                    ) : (
                      <Camera className="w-6 h-6 text-white" />
                    )}
                  </button>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="hidden"
                  />
                </div>
                <div>
                  <h1 className="text-3xl md:text-4xl font-extrabold text-white mb-1">
                    {(client.firstName || "") + " " + (client.lastName || "")}
                  </h1>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 items-center text-slate-300 text-sm md:text-base">
                    {clientStatus === 'active' && (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-300 border border-green-400/30">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></span>
                        {t('activeStatus')}
                      </span>
                    )}
                    {clientStatus === 'prospect' && (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-300 border border-yellow-400/30">
                        <span className="w-1.5 h-1.5 rounded-full bg-yellow-400"></span>
                        {t('prospectStatus')}
                      </span>
                    )}
                    {clientStatus === 'lost' && (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-500/20 text-red-300 border border-red-400/30">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-400"></span>
                        {t('lostStatus')}
                      </span>
                    )}
                    {customerSince && (
                      <span className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-[#FFB800]"></span>
                        {t('customerFor')} <b className="text-white">{customerSince}</b>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex flex-wrap gap-2">
              <button
                className="group relative flex items-center gap-2 bg-gradient-to-r from-[#FFB800] to-amber-500 hover:from-amber-500 hover:to-[#FFB800] text-white font-semibold px-5 py-2.5 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
                onClick={onCallDemo}
              >
                <Phone size={18} strokeWidth={2.5} />
                <span>{t('call')}</span>
              </button>
              <button
                className="group relative flex items-center gap-2 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white font-semibold px-5 py-2.5 rounded-xl border border-white/20 hover:border-white/30 shadow-lg transition-all duration-200 transform hover:scale-105"
                onClick={onSendTextDemo}
              >
                <MessageCircle size={18} strokeWidth={2.5} />
                <span>{t('text')}</span>
              </button>
              <button
                className="group relative flex items-center gap-2 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white font-semibold px-5 py-2.5 rounded-xl border border-white/20 hover:border-white/30 shadow-lg transition-all duration-200 transform hover:scale-105"
                onClick={onSendEmailDemo}
              >
                <Mail size={18} strokeWidth={2.5} />
                <span>{t('email')}</span>
              </button>
            </div>
          </div>

          {/* Secondary Actions Bar */}
          <div className="flex flex-wrap items-center gap-2 mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-white/10">
            <button
              className="flex items-center gap-2 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white font-medium px-4 py-2 rounded-lg border border-white/20 transition-all"
              onClick={onScheduleDemo}
            >
              <CalendarClock size={16} /> {t('scheduleReview')}
            </button>
            <button
              className="flex items-center gap-2 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white font-medium px-4 py-2 rounded-lg border border-white/20 transition-all"
              onClick={() => setShowTask(true)}
            >
              <CheckSquare size={16} /> {t('createTask')}
            </button>
            <button
              className="flex items-center gap-2 bg-orange-500/20 hover:bg-orange-500/30 text-orange-200 font-medium px-4 py-2 rounded-lg border border-orange-400/30 transition-all"
              onClick={() => setShowCallOutcome(true)}
            >
              <Phone size={16} /> Log Call Outcome
            </button>
            <Link
              to={`/service/client/${client.id}`}
              className="flex items-center gap-2 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 font-medium px-4 py-2 rounded-lg border border-emerald-400/30 transition-all"
            >
              <Headphones size={16} /> Service Workspace
            </Link>

            {/* Health Data Button */}
            {bbLoading ? (
              <div className="flex items-center gap-2 bg-blue-500/20 text-blue-200 font-medium px-4 py-2 rounded-lg border border-blue-400/30">
                <RefreshCw size={16} className="animate-spin" /> {t('loading')}
              </div>
            ) : bbStatus?.connected ? (
              <div className="flex items-center gap-1">
                <span className="flex items-center gap-2 bg-blue-500/30 text-blue-200 font-medium px-3 py-2 rounded-lg border border-blue-400/30">
                  <span className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></span>
                  Health Data Connected
                </span>
                <button
                  onClick={handleSyncHealth}
                  disabled={bbSyncing}
                  className="flex items-center gap-1 bg-blue-500/30 hover:bg-blue-500/40 text-blue-200 font-medium px-3 py-2 rounded-lg border border-blue-400/30 transition-all disabled:opacity-50"
                >
                  <RefreshCw size={14} className={bbSyncing ? 'animate-spin' : ''} />
                  {bbSyncing ? 'Syncing...' : 'Sync'}
                </button>
                {!showDisconnectConfirm ? (
                  <button
                    onClick={() => setShowDisconnectConfirm(true)}
                    className="flex items-center gap-1 bg-red-500/20 hover:bg-red-500/30 text-red-200 font-medium px-3 py-2 rounded-lg border border-red-400/30 transition-all"
                  >
                    <Unlink size={14} />
                  </button>
                ) : (
                  <div className="flex items-center gap-1 bg-red-500/30 px-2 py-1 rounded-lg border border-red-400/30">
                    <span className="text-xs text-red-200">Disconnect?</span>
                    <button onClick={handleDisconnectHealth} className="px-2 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded transition-colors">Yes</button>
                    <button onClick={() => setShowDisconnectConfirm(false)} className="px-2 py-1 text-xs font-medium text-slate-200 hover:bg-white/10 rounded transition-colors">No</button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={handleConnectHealth}
                className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-semibold px-5 py-2 rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
              >
                <Heart size={16} /> Connect Health Data
              </button>
            )}

            {/* Mark as Lost / Reactivate */}
            {clientStatus === 'active' && (
              <button
                className="flex items-center gap-1.5 ml-auto bg-red-500/20 hover:bg-red-500/30 text-red-200 text-sm font-medium px-3 py-1.5 rounded-lg border border-red-400/30 transition-all"
                onClick={() => setShowLostModal(true)}
              >
                <UserMinus size={14} /> {t('markAsLost')}
              </button>
            )}
            {clientStatus === 'lost' && (
              <button
                className="flex items-center gap-1.5 ml-auto bg-green-500/20 hover:bg-green-500/30 text-green-200 text-sm font-medium px-3 py-1.5 rounded-lg border border-green-400/30 transition-all"
                onClick={handleReactivateClient}
              >
                <UserMinus size={14} /> {t('reactivate')}
              </button>
            )}
          </div>
        </div>

        {/* Client Snapshot - Expandable card with key info */}
        <div className="mb-6">
          <ClientSnapshot client={client} onEdit={() => setShowEdit(true)} />
        </div>

        {/* Risk Score & Confidence Pulse - Side by Side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Risk Score Card */}
          <RiskScoreCard
            clientId={client.id}
            initialScore={client.riskScore}
            onScoreChange={(newScore) => setClient({ ...client, riskScore: newScore })}
          />

          {/* Confidence Pulse Card */}
          <ConfidencePulseCard
            clientId={client.id}
            clientName={`${client.firstName || ''} ${client.lastName || ''}`}
          />
        </div>

        {/* Health Data Dashboard - Comprehensive Medicare Blue Button Data */}
        <div className="mb-6">
          <HealthDataSection
            clientId={client.id}
            clientName={`${client.firstName || ''} ${client.lastName || ''}`}
            clientEmail={client.email}
            clientPhone={client.phone}
            clientCarrier={client.carrier}
          />
        </div>

        {/* Other Policies (Life Insurance, Ancillary, Annuities) */}
        <div className="mb-6">
          <OtherPoliciesCard
            clientId={client.id}
            onPoliciesChange={() => {
              // Refresh client data when policies change (may affect risk score)
              getClientById(id).then(data => setClient(data));
            }}
          />
        </div>

        {/* Plan History - Past Medicare plans */}
        <div className="mb-6">
          <PlanHistoryCard clientId={client.id} />
        </div>

        {/* Commission History */}
        <div className="mb-6">
          <ClientCommissionSection clientId={client.id} />
        </div>

        {/* Service Command Center - Carrier calls & outcomes */}
        <div className="mb-6">
          <ServiceTab
            clientId={client.id}
            clientName={`${client.firstName || ''} ${client.lastName || ''}`}
            clientCarrier={client.carrier}
          />
        </div>

        {/* Upcoming Tasks - Now at bottom before communications */}
        <div className="mb-6">
          <div className="bg-gradient-to-br from-white to-slate-50 rounded-2xl shadow-lg border border-slate-100 p-6 hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg text-[#172A3A] flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white">
                  <CheckSquare size={18} />
                </span>
                {t('upcomingTasks')}
              </h3>
              <button
                onClick={() => setShowTask(true)}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 hover:underline"
              >
                + {t('addTask')}
              </button>
            </div>
            {clientTasks.length > 0 ? (
              <div className="space-y-2">
                {clientTasks.filter(tk => tk.status !== 'completed' && tk.status !== 'done').map(task => (
                  <div key={task.id} className="bg-white rounded-xl border border-slate-200 px-4 py-3 flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-[#172A3A] truncate">{task.title}</div>
                      {task.dueDate && (
                        <div className="text-xs text-slate-400 mt-0.5">Due: {new Date(task.dueDate).toLocaleDateString()}</div>
                      )}
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      task.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                      task.priority === 'high' ? 'bg-red-100 text-red-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {task.status === 'in_progress' ? 'In Progress' : task.priority || 'pending'}
                    </span>
                  </div>
                ))}
                {clientTasks.filter(tk => tk.status !== 'completed' && tk.status !== 'done').length === 0 && (
                  <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
                    <div className="text-slate-400 text-sm">{t('noTasksScheduled')}</div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
                <div className="text-slate-400 text-sm">{t('noTasksScheduled')}</div>
              </div>
            )}
          </div>
        </div>

        {/* Notes, Communications, Documents */}
        <ClientDetailCard
          client={client}
          onClientUpdate={() => getClientById(id).then(data => setClient(data))}
          onViewComms={() => setShowComms(true)}
          onEdit={() => setShowEdit(true)}
        />

        {/* Modals / Drawers */}
        {showSms && (
          <MessageThread
            channel="sms"
            thread={[]}
            onClose={() => setShowSms(false)}
            unread={0}
            clientName={`${client.firstName || ""} ${client.lastName || ""}`}
            clientId={client.id}
            clientPhone={client.phone}
          />
        )}
        {showEmail && (
          <EmailComposer
            onClose={() => setShowEmail(false)}
            clientName={`${client.firstName || ""} ${client.lastName || ""}`}
            clientId={client.id}
            clientEmail={client.email}
          />
        )}
        {showSchedule && (
          <ClientScheduleModal onClose={() => setShowSchedule(false)} client={client} onSave={async ({ type, date }) => {
            await addComm(client.id, { type: "appointment", direction: "out", subject: type === "call" ? "Scheduled Call" : "Scheduled Appointment", preview: `Scheduled for ${date}` });
          }} />
        )}
        {showEdit && (
          <ClientEditModal open={true} client={client} onClose={() => setShowEdit(false)} onSave={handleSaveClient} saving={savingClient} />
        )}
        {showComms && (
          <CommunicationsHub
            clientId={client.id}
            clientName={`${client.firstName || ''} ${client.lastName || ''}`}
            clientPhone={client.phone}
            onClose={() => setShowComms(false)}
          />
        )}
        <CallTypeModal
          client={client}
          isOpen={showCallTypeModal}
          onClose={() => setShowCallTypeModal(false)}
          onCall={handleInitiateCall}
          onCallSuccess={() => setShowCallOutcome(true)}
        />
        <CallOutcomeModal
          isOpen={showCallOutcome}
          onClose={() => setShowCallOutcome(false)}
          client={client}
          onSuccess={() => {
            getClientById(id).then(data => setClient(data));
          }}
        />
        {showLostModal && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
            <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <UserMinus size={20} className="text-red-600" />
                </div>
                <div>
                  <div className="font-bold text-lg">{t('markClientLostTitle')}</div>
                  <p className="text-sm text-slate-500">
                    {t('markClientLostConfirm', { name: `${client.firstName} ${client.lastName}` })}
                  </p>
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-amber-800">{t('canReactivateLater')}</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('reasonForLosing')} <span className="text-red-500">*</span></label>
                <textarea
                  className="border rounded-xl px-3 py-2 w-full focus:ring-2 focus:ring-red-200 focus:border-red-400 outline-none"
                  rows={3}
                  value={lostReason}
                  onChange={(e) => setLostReason(e.target.value)}
                  placeholder={t('reasonPlaceholder')}
                />
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button className="px-4 py-2 rounded-xl hover:bg-gray-100 font-medium" onClick={() => { setShowLostModal(false); setLostReason(""); }}>
                  {t('cancel')}
                </button>
                <button
                  className="px-4 py-2 rounded-xl bg-red-600 text-white hover:bg-red-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleMarkAsLost}
                  disabled={!lostReason.trim()}
                >
                  {t('yesMarkAsLost')}
                </button>
              </div>
            </div>
          </div>
        )}
        {showTask && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
            <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl p-6">
              <div className="font-bold text-lg mb-4">{t('createTaskFor')} {client.firstName} {client.lastName}</div>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium mb-1">{t('title')}</label>
                  <input
                    className="border rounded-xl px-3 py-2 w-full"
                    value={taskData.title}
                    onChange={(e) => setTaskData(v => ({ ...v, title: e.target.value }))}
                    placeholder={t('callToDiscussPlaceholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{t('notes')}</label>
                  <textarea
                    className="border rounded-xl px-3 py-2 w-full"
                    rows={3}
                    value={taskData.notes}
                    onChange={(e) => setTaskData(v => ({ ...v, notes: e.target.value }))}
                    placeholder={t('additionalDetailsPlaceholder')}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('priority')}</label>
                    <select
                      className="border rounded-xl px-3 py-2 w-full"
                      value={taskData.priority}
                      onChange={(e) => setTaskData(v => ({ ...v, priority: e.target.value }))}
                    >
                      <option value="low">{t('low')}</option>
                      <option value="normal">{t('normal')}</option>
                      <option value="high">{t('high')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('dueDate')}</label>
                    <input
                      type="datetime-local"
                      className="border rounded-xl px-3 py-2 w-full"
                      value={taskData.dueDate}
                      onChange={(e) => setTaskData(v => ({ ...v, dueDate: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button className="px-4 py-2 rounded-xl hover:bg-gray-100" onClick={() => setShowTask(false)}>
                  {t('cancel')}
                </button>
                <button className="px-4 py-2 rounded-xl bg-black text-white hover:opacity-90" onClick={saveTask}>
                  {t('createTask')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Health Data Connection Modal */}
        {showConnectModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {connectModalStep === 'method' && (
                    <button
                      onClick={handleBackToSourceChoice}
                      className="text-gray-400 hover:text-gray-600 -ml-1"
                    >
                      <ChevronRight className="rotate-180" size={20} />
                    </button>
                  )}
                  <h3 className="text-lg font-semibold text-gray-900">
                    {connectModalStep === 'choose' ? 'Connect Health Data' :
                     selectedSource === 'bluebutton' ? 'Connect Medicare Blue Button' :
                     `Connect ${selectedSource?.name || 'Carrier'}`}
                  </h3>
                </div>
                <button
                  onClick={() => setShowConnectModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6">
                {linkSent ? (
                  /* Success Message */
                  <div className="text-center py-4">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="text-green-600" size={32} />
                    </div>
                    <h4 className="text-lg font-medium text-gray-900 mb-2">Link Sent!</h4>
                    <p className="text-gray-500 mb-4">
                      {linkSent === 'email'
                        ? `Authorization link sent to ${client.email || "client's email"}`
                        : `Authorization link sent to ${client.phone || "client's phone"}`
                      }
                    </p>
                    <p className="text-sm text-gray-400">
                      The client will receive instructions to connect their {selectedSource === 'bluebutton' ? 'Medicare' : selectedSource?.name} account.
                    </p>
                    <button
                      onClick={() => setShowConnectModal(false)}
                      className="mt-6 px-6 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                ) : connectModalStep === 'choose' ? (
                  /* Step 1: Choose Data Source */
                  <>
                    <p className="text-gray-500 mb-6 text-center">
                      Choose where to get {client.firstName}'s health data
                    </p>

                    {connectError && (
                      <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                        {connectError}
                      </div>
                    )}

                    <div className="space-y-3">
                      {/* Blue Button Option */}
                      <button
                        onClick={() => handleSelectSource('bluebutton')}
                        className="w-full p-4 rounded-xl border-2 border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition flex items-center gap-4"
                      >
                        <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                          <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                          </svg>
                        </div>
                        <div className="flex-1 text-left">
                          <div className="font-semibold text-gray-900 text-lg">Medicare Blue Button</div>
                          <div className="text-sm text-gray-500">Connect via Medicare.gov</div>
                          <div className="text-xs text-blue-600 mt-1">All Medicare claims & prescription data</div>
                        </div>
                        <ChevronRight className="text-gray-400" size={20} />
                      </button>

                      {/* Carrier Option - Only show if client has a matched carrier */}
                      {matchedCarrier && (
                        <button
                          onClick={() => handleSelectSource(matchedCarrier)}
                          className="w-full p-4 rounded-xl border-2 border-gray-200 hover:border-green-300 hover:bg-green-50 transition flex items-center gap-4"
                        >
                          <div className={`w-14 h-14 ${matchedCarrier.color || 'bg-green-600'} rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden`}>
                            {matchedCarrier.logo ? (
                              <img
                                src={matchedCarrier.logo}
                                alt={matchedCarrier.name}
                                className="w-10 h-10 object-contain"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <span className="text-white font-bold text-xl">
                                {matchedCarrier.name?.charAt(0) || 'C'}
                              </span>
                            )}
                          </div>
                          <div className="flex-1 text-left">
                            <div className="font-semibold text-gray-900 text-lg">{matchedCarrier.name}</div>
                            <div className="text-sm text-gray-500">Connect via carrier portal</div>
                            <div className="text-xs text-green-600 mt-1">Plan-specific benefits & health data</div>
                          </div>
                          <ChevronRight className="text-gray-400" size={20} />
                        </button>
                      )}
                    </div>
                  </>
                ) : (
                  /* Step 2: Choose Send Method (Email/SMS) */
                  <>
                    <p className="text-gray-500 mb-6 text-center">
                      How do you want to send the authorization link?
                    </p>

                    {connectError && (
                      <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                        {connectError}
                      </div>
                    )}

                    <div className="space-y-3">
                      {/* Email Option */}
                      <button
                        onClick={handleSendEmail}
                        disabled={sendingLink || !client.email}
                        className={`w-full p-4 rounded-xl border-2 transition flex items-center gap-4 ${
                          client.email
                            ? 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                            : 'border-gray-100 bg-gray-50 cursor-not-allowed'
                        }`}
                      >
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                          client.email ? 'bg-blue-100' : 'bg-gray-200'
                        }`}>
                          {sendingLink === 'email' ? (
                            <RefreshCw className="animate-spin text-blue-600" size={24} />
                          ) : (
                            <Mail className={client.email ? 'text-blue-600' : 'text-gray-400'} size={24} />
                          )}
                        </div>
                        <div className="flex-1 text-left">
                          <div className={`font-medium ${client.email ? 'text-gray-900' : 'text-gray-400'}`}>
                            Send via Email
                          </div>
                          <div className="text-sm text-gray-500">
                            {client.email || 'No email on file'}
                          </div>
                        </div>
                        {client.email && (
                          <ChevronRight className="text-gray-400" size={20} />
                        )}
                      </button>

                      {/* SMS Option */}
                      <button
                        onClick={handleSendSMS}
                        disabled={sendingLink || !client.phone}
                        className={`w-full p-4 rounded-xl border-2 transition flex items-center gap-4 ${
                          client.phone
                            ? 'border-gray-200 hover:border-green-300 hover:bg-green-50'
                            : 'border-gray-100 bg-gray-50 cursor-not-allowed'
                        }`}
                      >
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                          client.phone ? 'bg-green-100' : 'bg-gray-200'
                        }`}>
                          {sendingLink === 'sms' ? (
                            <RefreshCw className="animate-spin text-green-600" size={24} />
                          ) : (
                            <MessageCircle className={client.phone ? 'text-green-600' : 'text-gray-400'} size={24} />
                          )}
                        </div>
                        <div className="flex-1 text-left">
                          <div className={`font-medium ${client.phone ? 'text-gray-900' : 'text-gray-400'}`}>
                            Send via Text Message
                          </div>
                          <div className="text-sm text-gray-500">
                            {client.phone || 'No phone on file'}
                          </div>
                        </div>
                        {client.phone && (
                          <ChevronRight className="text-gray-400" size={20} />
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientProfile;
